/**
 * editorDoc.js — the manual Layout Editor's document model.
 *
 * Pure data + geometry. No React, no DOM, no transport — unit-testable in node
 * (mirrored to editorDoc.cjs by tools/gen-cjs-mirrors.cjs).
 *
 * WHY THIS EXISTS
 * ---------------
 * The nest is very good and not always perfect. An operator who can see the
 * one carton that should move 6 mm left has, until now, had exactly one lever:
 * re-run the whole layout and hope. This module is the model behind a screen
 * where they can move it, and it is built so the edited sheet leaves through
 * the SAME door as a nested one — POST /export/vector/pdf.
 *
 * ═══ COORDINATE MODEL (contract v1.2 §3.5, §4.2) ═══════════════════════════
 *
 * Everything here is SHEET MILLIMETRES, Y-UP, origin at the sheet's lower-left
 * — MNE's frame and the PDF endpoint's frame. The Y-flip to SVG is the
 * renderer's job, not the model's (VectorLayoutView.jsx does `shH - y`).
 *
 * A placement in the wire format is a polygon with rotation ALREADY BAKED IN:
 * `placed_vertices` are final and `rotation_deg` is a record of what was done,
 * not an instruction. That is unusable as an editable model — you cannot ask a
 * baked polygon to turn 15° more without knowing what it looked like at 0°.
 *
 * So the doc splits every placement in two:
 *
 *   PROTOTYPE  the piece's shape at rotation 0, translated so its bounding-box
 *              minimum sits on the origin. Shared by every copy of that piece.
 *   PIECE      { proto, x, y, rot } — where one copy of that shape sits.
 *
 * and the placed polygon is rebuilt on demand:
 *
 *   placed = rotate(proto.outline, rot)  then translated so its bbox min = (x, y)
 *
 * `(x, y)` is therefore ALWAYS the lower-left of the placed, rotated bounding
 * box — which is precisely the `x_mm`/`y_mm` that buildPressPdfPayload derives
 * with `Math.min(...)` and precisely what PlacementIn wants. The edited doc and
 * a nested result reduce to the same four numbers, so the export path needs no
 * second mode.
 *
 * Ingesting the reverse direction (buildEditorDoc) un-bakes: it rotates
 * `placed_vertices` by −rotation_deg to recover the 0° shape. The round trip
 * is exact — rotate by −θ then +θ and translate back — so loading a layout and
 * exporting it untouched reproduces the engine's own geometry.
 *
 * ═══ WHY PROTOTYPES ARE KEYED ON GEOMETRY ══════════════════════════════════
 *
 * `piece_name` is the natural prototype key, and it is not sufficient. A BL
 * cross-pair emits `variant` components under one piece name, so two placements
 * can share a name and NOT share a shape. Keying on name alone would let the
 * first-seen shape stand in for the second, silently cutting the wrong die. The
 * key therefore carries a fingerprint of the 0° outline, and genuinely
 * different shapes get separate prototypes even under one name.
 *
 * ═══ TILES ═════════════════════════════════════════════════════════════════
 *
 * A prepress tile is ONE placed piece worth TWO cartons, and `components[]`
 * holds each carton's own outline. Both travel on the prototype and are
 * transformed together by the same matrix, so the interlock cannot drift apart
 * under editing, and `cartons` keeps the count honest: deleting one tile
 * removes two cartons from the sheet, and the stats say so.
 */

import {
  placementCartons, placementSourceLabel, placementCartonCount,
} from './prepress';

/** Doc schema version — bumped when the persisted shape changes. */
export const EDITOR_DOC_VERSION = 1;

/**
 * Geometric tolerance, mm. One nanometre: orders of magnitude below anything a
 * press can hold, and orders of magnitude above the ~1e-13 mm of floating-point
 * dirt a rotate-and-translate leaves behind. Everything that asks "is this
 * exactly touching?" asks it through this.
 */
export const GEOM_EPS = 1e-6;

/* ══════════════════════════════════════════════════════════════════════════
 * Geometry primitives
 * ════════════════════════════════════════════════════════════════════════ */

/** Fold an angle into [0, 360). */
export function normalizeAngle(deg) {
  const d = Number(deg);
  if (!Number.isFinite(d)) return 0;
  return ((d % 360) + 360) % 360;
}

/**
 * Rotate one point CCW about the origin.
 *
 * Quarter turns are special-cased to EXACT integer arithmetic. `Math.cos(π/2)`
 * is 6.1e-17, not 0, so a 90° rotation through the general formula leaves a
 * 700 mm sheet coordinate carrying ~4e-14 mm of dirt. It is far below any
 * tolerance that matters, but it also turns 0 into -0 and clean die
 * coordinates into 17-digit noise in every payload and every test expectation.
 * Quarter turns are the overwhelmingly common case, so keep them exact.
 */
export function rotatePoint(x, y, deg) {
  const a = normalizeAngle(deg);
  if (a === 0) return [x, y];
  if (a === 90) return [-y, x];
  if (a === 180) return [-x, -y];
  if (a === 270) return [y, -x];
  const r = (a * Math.PI) / 180;
  const c = Math.cos(r), s = Math.sin(r);
  return [x * c - y * s, x * s + y * c];
}

/** Rotate a vertex list CCW about the origin. */
export function rotatePoints(pts, deg) {
  const a = normalizeAngle(deg);
  if (a === 0) return pts.map((p) => [p[0], p[1]]);
  return pts.map((p) => rotatePoint(p[0], p[1], a));
}

/** Translate a vertex list. */
export function translatePoints(pts, dx, dy) {
  return pts.map((p) => [p[0] + dx, p[1] + dy]);
}

/** Axis-aligned bounds of a vertex list → { x0, y0, x1, y1, w, h }, or null. */
export function bboxOf(pts) {
  if (!Array.isArray(pts) || pts.length === 0) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of pts) {
    if (p[0] < x0) x0 = p[0];
    if (p[0] > x1) x1 = p[0];
    if (p[1] < y0) y0 = p[1];
    if (p[1] > y1) y1 = p[1];
  }
  return { x0, y0, x1, y1, w: x1 - x0, h: y1 - y0 };
}

/** Signed-area magnitude of a simple polygon (shoelace), mm². */
export function polygonArea(pts) {
  if (!Array.isArray(pts) || pts.length < 3) return 0;
  let s = 0;
  for (let i = 0, n = pts.length; i < n; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(s) / 2;
}

/** Do two axis-aligned boxes come within `pad` of each other? */
function bboxesNear(a, b, pad = 0) {
  return !(a.x1 + pad < b.x0 || b.x1 + pad < a.x0
        || a.y1 + pad < b.y0 || b.y1 + pad < a.y0);
}

/** Distance from point P to closed segment AB. */
function pointSegmentDistance(p, a, b) {
  const vx = b[0] - a[0], vy = b[1] - a[1];
  const wx = p[0] - a[0], wy = p[1] - a[1];
  const len2 = vx * vx + vy * vy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, (wx * vx + wy * vy) / len2)) : 0;
  const dx = wx - t * vx, dy = wy - t * vy;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Is a point strictly inside a simple polygon? (ray cast, concave-safe) */
export function pointInPolygon(pt, poly) {
  const [px, py] = pt;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    const straddles = (yi > py) !== (yj > py);
    if (straddles && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * Shortest distance from a point to a polygon's boundary.
 * Zero on the boundary, positive both inside and outside.
 */
function distanceToBoundary(pt, poly) {
  let best = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const d = pointSegmentDistance(pt, poly[i], poly[(i + 1) % poly.length]);
    if (d < best) best = d;
  }
  return best;
}

/**
 * Inside the polygon and NOT on its edge.
 *
 * Plain ray casting is unreliable for a point sitting exactly on a boundary —
 * and that is not an exotic case here, it is the normal one: two interlocked
 * tile halves share a cut line, so every vertex of one lies on the other's
 * edge. Treating those as "inside" would report every tile in the job as an
 * overlap.
 */
function pointStrictlyInside(pt, poly) {
  return pointInPolygon(pt, poly) && distanceToBoundary(pt, poly) > GEOM_EPS;
}

/**
 * A point guaranteed to be in the polygon's interior.
 *
 * Every simple polygon has an ear (the two-ears theorem), so for some vertex
 * the diagonal skipping it lies wholly inside and its midpoint is interior.
 * The centroid is NOT a substitute: for an L-shaped die it can land in the
 * notch, i.e. outside the carton entirely.
 */
function interiorPoint(poly) {
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const a = poly[i], c = poly[(i + 2) % n];
    const mid = [(a[0] + c[0]) / 2, (a[1] + c[1]) / 2];
    if (pointStrictlyInside(mid, poly)) return mid;
  }
  return null;
}

/**
 * Every parameter t in [0,1] at which segment AB meets segment CD — crossings,
 * endpoint grazes and the span of a collinear overlap alike.
 *
 * Grazes are wanted here even though they are not crossings: they are where an
 * edge ENTERS or LEAVES the other polygon, and those are exactly the cuts
 * polygonsOverlap needs in order to probe between them.
 */
function edgeHitParams(a, b, c, d) {
  const rx = b[0] - a[0], ry = b[1] - a[1];
  const sx = d[0] - c[0], sy = d[1] - c[1];
  const denom = rx * sy - ry * sx;
  const qpx = c[0] - a[0], qpy = c[1] - a[1];

  if (Math.abs(denom) > GEOM_EPS) {
    const t = (qpx * sy - qpy * sx) / denom;
    const u = (qpx * ry - qpy * rx) / denom;
    return (t >= -GEOM_EPS && t <= 1 + GEOM_EPS && u >= -GEOM_EPS && u <= 1 + GEOM_EPS)
      ? [Math.min(1, Math.max(0, t))]
      : [];
  }
  // Parallel. Only a COLLINEAR pair can contribute, and it contributes the
  // span of the shared run, so the probe points land off it rather than on it.
  if (Math.abs(qpx * ry - qpy * rx) > GEOM_EPS) return [];
  const len2 = rx * rx + ry * ry;
  if (len2 <= GEOM_EPS) return [];
  const t0 = (qpx * rx + qpy * ry) / len2;
  const t1 = ((d[0] - a[0]) * rx + (d[1] - a[1]) * ry) / len2;
  const lo = Math.max(0, Math.min(t0, t1));
  const hi = Math.min(1, Math.max(t0, t1));
  return hi < lo - GEOM_EPS ? [] : [lo, hi];
}

/** Does any part of A's boundary run through B's interior? */
function boundaryEntersInterior(a, b) {
  for (let i = 0; i < a.length; i++) {
    const p = a[i], q = a[(i + 1) % a.length];
    // Cut this edge wherever it meets B's boundary. Between two consecutive
    // cuts the edge is entirely inside, entirely outside, or lying along B's
    // boundary — so ONE midpoint probe per span decides it.
    const cuts = [0, 1];
    for (let j = 0; j < b.length; j++) {
      for (const t of edgeHitParams(p, q, b[j], b[(j + 1) % b.length])) cuts.push(t);
    }
    cuts.sort((m, n) => m - n);
    for (let k = 1; k < cuts.length; k++) {
      if (cuts[k] - cuts[k - 1] < GEOM_EPS) continue;
      const t = (cuts[k] + cuts[k - 1]) / 2;
      if (pointStrictlyInside([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t], b)) return true;
    }
  }
  return false;
}

/**
 * Do two simple polygons share INTERIOR area?
 *
 * Interior, not boundary — dies meeting exactly along a shared knife line are a
 * legal butt cut at zero kerf, and at non-zero kerf the clearance rule below
 * already has something to say about them. Reporting those as "the press cannot
 * cut this" would make the editor cry wolf on the tidiest layout there is, and
 * on every prepress tile's own interlock.
 *
 * Two probes:
 *
 *   1. does either boundary pass through the other's interior? Each edge is cut
 *      where it meets the other outline and one midpoint per span is tested.
 *   2. otherwise, is one interior point of either inside the other? This is the
 *      containment case — including a die dropped EXACTLY on an identical die,
 *      which the Duplicate button produces every time before the copy moves.
 *
 * The naive version of (1) — "do two edges cross transversally?" — is what an
 * overlap test usually is, and it is wrong here in the most ordinary situation
 * on a press sheet. Two cartons of equal height sitting in one row share their
 * top and bottom lines exactly; drag one 2 mm into its neighbour and NOTHING
 * crosses transversally and NO vertex is strictly inside the other. Every
 * corner of the 2 mm overlap sits precisely on both outlines. Cutting the edges
 * at their grazes and probing BETWEEN the cuts is what sees it.
 *
 * All of it works on concave outlines, which is the point: an L-shaped die's
 * notch is empty space and a neighbour is allowed to sit in it.
 */
export function polygonsOverlap(a, b) {
  if (!a || !b || a.length < 3 || b.length < 3) return false;
  if (boundaryEntersInterior(a, b) || boundaryEntersInterior(b, a)) return true;
  const ia = interiorPoint(a);
  if (ia && pointStrictlyInside(ia, b)) return true;
  const ib = interiorPoint(b);
  if (ib && pointStrictlyInside(ib, a)) return true;
  return false;
}

/**
 * Shortest distance between two polygon BOUNDARIES. 0 when they touch or
 * overlap. This is the number kerf is a rule about: kerf is inter-die spacing,
 * not polygon inflation (contract v1.2 §6), so a manual move is legal exactly
 * when the gap it leaves is still >= kerf.
 */
export function polygonDistance(a, b) {
  if (polygonsOverlap(a, b)) return 0;
  return boundaryDistance(a, b);
}

/**
 * Shortest distance between two polygon boundaries, WITHOUT first asking
 * whether they overlap. Callers that have already established they do not
 * (computeConflicts, the tile slider) use this to avoid paying for the overlap
 * test twice on every pair.
 */
function boundaryDistance(a, b) {
  let best = Infinity;
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i], a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const b1 = b[j], b2 = b[(j + 1) % b.length];
      const d = Math.min(
        pointSegmentDistance(a1, b1, b2), pointSegmentDistance(a2, b1, b2),
        pointSegmentDistance(b1, a1, a2), pointSegmentDistance(b2, a1, a2),
      );
      if (d < best) best = d;
      if (best === 0) return 0;
    }
  }
  return best;
}

/* ══════════════════════════════════════════════════════════════════════════
 * Prototypes
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * Canonical form of a ring: quantised to 0.01 mm, wound CCW, and cyclically
 * rotated to start at its lexicographically smallest vertex.
 *
 * All three steps are load-bearing. The same die ingested from a 0° placement
 * and from a 90° one arrives as the same SET of local vertices in a different
 * cyclic ORDER — un-baking rotates the ring's starting point along with the
 * shape. Fingerprinting the raw list would call those two different shapes and
 * split one carton into two entries in the Add palette, each rotatable only
 * back to the angle it came from.
 */
function canonicalRing(pts) {
  const q = pts.map((p) => [Math.round(p[0] * 100), Math.round(p[1] * 100)]);
  let area2 = 0;
  for (let i = 0; i < q.length; i++) {
    const a = q[i], b = q[(i + 1) % q.length];
    area2 += a[0] * b[1] - b[0] * a[1];
  }
  const ring = area2 < 0 ? q.slice().reverse() : q;
  let k = 0;
  for (let i = 1; i < ring.length; i++) {
    if (ring[i][0] < ring[k][0] || (ring[i][0] === ring[k][0] && ring[i][1] < ring[k][1])) k = i;
  }
  return ring.slice(k).concat(ring.slice(0, k));
}

/** Stable, order-independent fingerprint of a 0° outline (0.01 mm grid). */
function shapeFingerprint(pts) {
  let h = 2166136261;
  for (const p of canonicalRing(pts)) {
    const s = `${p[0]},${p[1]};`;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
  }
  return (h >>> 0).toString(36);
}

/** Translate a vertex list so its bbox minimum lands on the origin. */
function toLocalOrigin(pts) {
  const bb = bboxOf(pts);
  if (!bb) return { pts: [], dx: 0, dy: 0 };
  return { pts: translatePoints(pts, -bb.x0, -bb.y0), dx: -bb.x0, dy: -bb.y0 };
}

/**
 * Un-bake one placement into { prototype-shaped geometry, x, y, rot }.
 *
 * The outline and every component are put through ONE transform — rotate by
 * −rotation_deg, then the single translation that lands the OUTLINE's bbox on
 * the origin. Normalising each component to its own origin instead would rotate
 * a tile's two halves into the right shape and the wrong place.
 */
function unbakePlacement(placement, sources) {
  const outline = Array.isArray(placement?.placed_vertices) ? placement.placed_vertices : [];
  if (outline.length < 3) return null;
  const rot = normalizeAngle(placement.rotation_deg || 0);

  const unrotated = rotatePoints(outline, -rot);
  const { pts: local, dx, dy } = toLocalOrigin(unrotated);

  const comps = placementCartons(placement)
    .filter((c) => !c.fused)          // fused === "this IS the outline", no split to carry
    .map((c) => ({
      role: c.role,
      member: c.member,
      vertices: translatePoints(rotatePoints(c.vertices, -rot), dx, dy),
    }));

  const bb = bboxOf(outline);
  const ti = Number.isInteger(placement.type_index) ? placement.type_index : 0;
  return {
    local,
    components: comps,
    x: bb.x0,
    y: bb.y0,
    rot,
    // Same precedence as placementSourceLabel: `source_id` is authoritative and
    // indexing `sources[]` is the fallback for backends that predate it — where
    // nothing has tiled, so the indices still line up. The prototype must end up
    // knowing its upload either way, because the export filters sources by it.
    sourceId: placement.source_id || (Array.isArray(sources) ? sources[ti]?.source_id : null) || null,
    pieceName: placement.piece_name || null,
    typeIndex: ti,
    cartons: placementCartonCount(placement),
    label: placementSourceLabel(placement, sources),
  };
}

/** The key under which two placements may share one prototype. */
function protoKeyFor(u) {
  const base = u.pieceName || u.sourceId || `type${u.typeIndex}`;
  return `${base}#${shapeFingerprint(u.local)}`;
}

/* ══════════════════════════════════════════════════════════════════════════
 * Building the document
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * The emitted BLANK outline for every carton prepress touched, by name.
 *
 * A `null` value means "prepress ran for this carton and we do not have its
 * blank" — a different answer from absent, which means prepress never touched
 * it and its own die is the truth. The caller must distinguish them: offering
 * the bare die for the first case puts a carton with no flaps on the sheet.
 */
function prepressBlanks(result) {
  const out = new Map();
  const cartons = result?.prepress_report?.cartons;
  if (!Array.isArray(cartons)) return out;
  for (const entry of cartons) {
    if (!entry || typeof entry !== 'object') continue;
    const verts = entry.assets?.blank?.vertices_mm;
    out.set(String(entry.name),
      (Array.isArray(verts) && verts.length >= 3) ? verts : null);
  }
  return out;
}

/** No reserved strip on any edge. */
export const ZERO_MARGINS = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * Are all four edges the same? Decides which editor a saved margin opens in,
 * and whether "uniform" is still an honest description of it.
 */
export function marginsAreUniform(sides) {
  const m = sides || ZERO_MARGINS;
  return m.top === m.right && m.right === m.bottom && m.bottom === m.left;
}

/**
 * Collapse four edges to one.
 *
 * Takes the LARGEST, and that is the whole content of the function: switching a
 * per-side margin back to uniform has to pick a number, and any other pick
 * silently reduces a clearance someone set on purpose. A gripper cut from 12 mm
 * to 0 because the operator toggled a control is a plate that jams the press.
 */
export function collapseToUniform(sides) {
  const m = sides || ZERO_MARGINS;
  return Math.max(m.top, m.right, m.bottom, m.left);
}

/**
 * The collapse rule the backend enforces with a 422: opposite edges may not eat
 * the whole sheet between them. Returns a message or null.
 *
 * Checked on the client so the operator hears it while typing rather than after
 * a round trip — and checked in the EDITOR too, which can reach the same
 * invalid state from the other direction by shrinking the sheet under a margin
 * that was fine when it was set.
 */
export function marginCollapseError(sides, width, height, fmt = (v) => `${v} mm`) {
  const m = sides || ZERO_MARGINS;
  if (m.bottom + m.top >= height) {
    return `Gripper + tail (${fmt(m.bottom)} + ${fmt(m.top)}) must be less than the sheet height (${fmt(height)}).`;
  }
  if (m.left + m.right >= width) {
    return `Guides (${fmt(m.left)} + ${fmt(m.right)}) must be less than the sheet width (${fmt(width)}).`;
  }
  return null;
}

/** {source_id: carton_type} for every carton prepress recognised. */
function prepressCartonTypes(result) {
  const out = new Map();
  for (const e of (result?.prepress_report?.cartons || [])) {
    if (e && e.carton_type) out.set(String(e.name), e.carton_type);
  }
  return out;
}

/** Normalise a margins dict, dropping it entirely when every edge is zero. */
function normalizeMargins(m) {
  if (!m || typeof m !== 'object') return null;
  const out = {
    top: Math.max(0, Number(m.top) || 0),
    right: Math.max(0, Number(m.right) || 0),
    bottom: Math.max(0, Number(m.bottom) || 0),
    left: Math.max(0, Number(m.left) || 0),
  };
  return Object.values(out).some((v) => v > 0) ? out : null;
}

/**
 * Build an editable document from an /export/vector response.
 *
 * `result` is not mutated and not retained by reference beyond `sources`, which
 * IS carried verbatim: the PDF endpoint re-resolves each die from
 * `die_source`, so dropping it would make an edited sheet unexportable.
 *
 * Sources that placed nothing still contribute a prototype (from their own
 * `vertices`, which are the same local Y-up mm the nest was handed). That is
 * deliberate: "the engine could not fit this, place it by hand" is one of the
 * two reasons this screen exists.
 */
export function buildEditorDoc(result, opts = {}) {
  if (!result || !Array.isArray(result.placements) || !result.sheet) {
    throw new Error('buildEditorDoc: need a result with sheet and placements[]');
  }
  const sources = Array.isArray(result.sources) ? result.sources : [];
  const protos = [];
  const byKey = new Map();
  const pieces = [];
  let uid = 1;

  for (const p of result.placements) {
    const u = unbakePlacement(p, sources);
    if (!u) continue;
    const key = protoKeyFor(u);
    if (!byKey.has(key)) {
      const bb = bboxOf(u.local);
      byKey.set(key, protos.length);
      protos.push({
        key,
        sourceId: u.sourceId,
        pieceName: u.pieceName,
        typeIndex: u.typeIndex,
        label: u.label,
        cartons: u.cartons,
        outline: u.local,
        components: u.components,
        w: bb.w,
        h: bb.h,
        origin: 'placement',
      });
    }
    pieces.push({ uid: `p${uid++}`, proto: key, x: u.x, y: u.y, rot: u.rot });
  }

  // Sources the nest never placed — addable, but nothing on the sheet yet.
  //
  // `sources[].vertices` is the ARTWORK die: it is built before prepress runs
  // (pipeline.py), so for a prepress carton it is the bare die WITHOUT the
  // flaps the press will cut. Offering that as an addable piece would put an
  // unflapped carton on the sheet, nested against neighbours by the wrong
  // outline. Where prepress published the blank, that is used instead; where it
  // ran and published nothing, the source is left out rather than offered
  // wrong.
  const blanks = prepressBlanks(result);
  // Carton FAMILY per source, so "select every RTE on this sheet" is a lookup
  // rather than a walk back through the report at every click.
  const families = prepressCartonTypes(result);
  protos.forEach((t) => { t.cartonType = families.get(t.sourceId) || null; });
  const placedSources = new Set(protos.map((t) => t.sourceId).filter(Boolean));
  sources.forEach((s, i) => {
    if (!s || placedSources.has(s.source_id)) return;
    const blank = blanks.get(s.source_id);
    if (blank === null) return;                    // prepress ran, blank unknown
    const verts = blank || (Array.isArray(s.vertices) ? s.vertices : []);
    if (verts.length < 3) return;
    const { pts: local } = toLocalOrigin(verts);
    const bb = bboxOf(local);
    const key = `${s.source_id}#${shapeFingerprint(local)}`;
    if (byKey.has(key)) return;
    byKey.set(key, protos.length);
    protos.push({
      key,
      sourceId: s.source_id,
      pieceName: null,
      typeIndex: i,
      label: s.source_id,
      cartons: 1,
      outline: local,
      components: [],
      w: bb.w,
      h: bb.h,
      origin: 'source',
      cartonType: families.get(s.source_id) || null,
    });
  });

  return {
    v: EDITOR_DOC_VERSION,
    jobId: result.job_id || null,
    originJobId: result.job_id || null,
    // Carried so the edited sheet can still place ARTWORK rather than blanks.
    // When prepress ran, the piece on the sheet is the flapped blank and the
    // die sits inside it at an offset; the chain that says where lives here and
    // nowhere else the editor can reach. See artworkPlacement.js.
    prepressReport: result.prepress_report || null,
    sheet: {
      width: Number(result.sheet.width) || 0,
      height: Number(result.sheet.height) || 0,
      margins: normalizeMargins(result.sheet.margins),
      kerf: Math.max(0, Number(opts.kerf ?? result.sheet.kerf) || 0),
    },
    sources,
    prototypes: protos,
    pieces,
    nextUid: uid,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
 * The editor's own desk
 *
 * The editor used to be reachable only through "Open in editor", and the
 * cartons it could place were whatever that one nest happened to contain. That
 * made the palette a read-only view of somebody else's bench: a carton the
 * operator wanted to add by hand had to be added in Auto Imposer, re-nested,
 * and re-opened — three steps to place one die.
 *
 * So the editor keeps a desk of its own. `createEditorDoc` gives it a sheet to
 * work on with nothing on it, and `addSourceCarton` puts one detected carton on
 * that desk: its SOURCE RECORD (which the PDF endpoint re-resolves the artwork
 * from) and a prototype the palette can place. `removeSourceCarton` takes it
 * off again, with everything it had put on the sheet.
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * A document with a sheet and nothing on it.
 *
 * `originJobId` is deliberately null: this sheet did not come from a nest, so
 * there is no job to point an export back at, and claiming one would attach an
 * edited sheet to a run it was never part of.
 */
export function createEditorDoc({ width = 0, height = 0, kerf = 0, margins = null } = {}) {
  return {
    v: EDITOR_DOC_VERSION,
    jobId: null,
    originJobId: null,
    prepressReport: null,
    sheet: {
      width: Math.max(0, Number(width) || 0),
      height: Math.max(0, Number(height) || 0),
      margins: normalizeMargins(margins),
      kerf: Math.max(0, Number(kerf) || 0),
    },
    sources: [],
    prototypes: [],
    pieces: [],
    nextUid: 1,
  };
}

/**
 * Put one carton on the desk — source record plus an addable prototype.
 *
 * `source` is a SourceResult, the same shape /extract/detect-die returns and
 * the same shape `buildEditorDoc` carries in `doc.sources`: it is what
 * `docToVectorResult` emits and what the PDF endpoint matches its file parts
 * against, so a carton added here plates through exactly the path a nested one
 * does.
 *
 * WHAT GETS PLACED is `blank` when prepress has built one, and the detected die
 * otherwise. That distinction is the whole of `buildEditorDoc`'s refusal to
 * offer a bare die for a carton prepress has run on: the artwork outline has no
 * flaps, so placing it nests the carton by the wrong shape and cuts a carton
 * that cannot be made up. On the desk the operator is ASKED (see `flapsMissing`)
 * and the answer arrives here — either as a blank with its report entry, or as
 * a deliberate "place it as drawn".
 *
 * `reportEntry` is not optional decoration when a blank is passed. The die sits
 * INSIDE the blank at an offset only `transforms` knows, so an entry-less blank
 * would plate every carton's artwork in the wrong place. It is merged into
 * `doc.prepressReport` under the carton's own name, which is the key
 * `cartonTypeFor` and the artwork chain look it up by.
 *
 * Idempotent by source_id: re-adding a carton already on the desk is a no-op
 * that returns the existing prototype's key, so a double-click on a library row
 * cannot put two of the same die in the palette.
 *
 * @returns {{ doc: Object, key: string|null, added: boolean }}
 */
export function addSourceCarton(doc, source, { label = null, blank = null, reportEntry = null } = {}) {
  if (!doc) return { doc, key: null, added: false };
  const sourceId = source?.source_id ? String(source.source_id) : null;
  const die = Array.isArray(source?.vertices) ? source.vertices : [];
  if (!sourceId || die.length < 3) return { doc, key: null, added: false };

  const existing = doc.prototypes.find((t) => t.sourceId === sourceId);
  if (existing) return { doc, key: existing.key, added: false };

  // The blank when prepress built one, the die when it did not. A blank too
  // short to be a polygon is ignored rather than trusted — falling back to the
  // die places a carton that is merely unflapped, where honouring it would
  // place a prototype with no shape at all.
  const verts = (Array.isArray(blank) && blank.length >= 3) ? blank : die;
  const flapped = verts !== die;

  const { pts: local } = toLocalOrigin(verts);
  const bb = bboxOf(local);
  if (!bb) return { doc, key: null, added: false };
  const key = `${sourceId}#${shapeFingerprint(local)}`;
  if (doc.prototypes.some((t) => t.key === key)) return { doc, key, added: false };

  // The source list is append-only for the life of a carton on the desk, so an
  // existing prototype's `typeIndex` keeps pointing at the record it named.
  // `docToVectorResult` re-maps indices against the sources it actually emits
  // anyway, but a stable index here means the doc is never internally wrong.
  const sources = doc.sources.some((s) => s.source_id === sourceId)
    ? doc.sources
    : [...doc.sources, source];

  return {
    doc: {
      ...doc,
      sources,
      // The entry travels with the blank or not at all. An entry without a
      // blank would tell the export chain to look for a flapped die that is
      // not on the sheet.
      prepressReport: (flapped && reportEntry)
        ? mergeCartonIntoReport(doc.prepressReport, sourceId, reportEntry)
        : doc.prepressReport,
      prototypes: [...doc.prototypes, {
        key,
        sourceId,
        pieceName: null,
        typeIndex: sources.length - 1,
        label: label || source.name || sourceId,
        cartons: 1,
        outline: local,
        components: [],
        w: bb.w,
        h: bb.h,
        origin: flapped ? 'desk-blank' : 'desk',
        cartonType: flapped ? (reportEntry?.carton_type || null) : null,
      }],
    },
    key,
    added: true,
  };
}

/**
 * File one carton's prepress entry in the document's report, under its name.
 *
 * `mergeTileIntoReport` is the sibling of this and deliberately narrower: a
 * tile arrives for a carton the report ALREADY describes, so it merges two
 * keys and leaves the rest of the entry alone. A desk carton is the opposite
 * case — nothing in the document has ever heard of it — so the whole entry is
 * the truth and replaces anything filed under that name.
 */
export function mergeCartonIntoReport(report, name, entry) {
  if (!entry || !name) return report;
  const cartons = Array.isArray(report?.cartons) ? report.cartons : [];
  const filed = { ...entry, name: String(name) };
  const i = cartons.findIndex((c) => c && String(c.name) === String(name));
  const out = i < 0 ? [...cartons, filed] : cartons.map((c, k) => (k === i ? filed : c));
  return { ...(report || {}), cartons: out };
}

/**
 * Take a carton off the desk, with every piece it put on the sheet.
 *
 * Leaving the pieces behind is not an option: their prototype would be gone and
 * `pieceGeometry` would return null for them, which is a piece that is on the
 * plate, invisible on screen, and checked by nothing. Tiles built FROM the
 * carton go with it for the same reason — a pair of a die whose artwork has
 * left the desk cannot be plated either.
 */
export function removeSourceCarton(doc, sourceId) {
  if (!doc || !sourceId) return doc;
  const sid = String(sourceId);
  const doomed = new Set(
    doc.prototypes
      .filter((t) => t.sourceId === sid || (t.members || []).some((m) => m.sourceId === sid))
      .map((t) => t.key),
  );
  if (!doomed.size && !doc.sources.some((s) => s.source_id === sid)) return doc;
  // The prepress entry goes too. A report describing a carton the sheet no
  // longer has is not merely untidy: `unplaceableCartons` walks the report, so
  // a stale entry for a removed die can block an export over geometry nothing
  // is going to plate.
  const cartons = doc.prepressReport?.cartons;
  return {
    ...doc,
    sources: doc.sources.filter((s) => s.source_id !== sid),
    prototypes: doc.prototypes.filter((t) => !doomed.has(t.key)),
    pieces: doc.pieces.filter((p) => !doomed.has(p.proto)),
    ...(Array.isArray(cartons) ? {
      prepressReport: {
        ...doc.prepressReport,
        cartons: cartons.filter((c) => String(c?.name) !== sid),
      },
    } : {}),
  };
}

/** Prototype lookup by key. */
export function protoOf(doc, piece) {
  const k = typeof piece === 'string' ? piece : piece?.proto;
  return doc?.prototypes?.find((t) => t.key === k) || null;
}

/**
 * The placed geometry of one piece — outline, per-carton components, bbox.
 *
 * This is the single place the model bakes rotation, and every consumer
 * (renderer, conflict checker, exporter) goes through it. A second baking site
 * is a second chance for the picture and the plate to disagree.
 */
export function pieceGeometry(doc, piece) {
  const t = protoOf(doc, piece);
  if (!t) return null;
  const rot = normalizeAngle(piece.rot);
  const rotated = rotatePoints(t.outline, rot);
  const rotComps = t.components.map((c) => ({
    role: c.role,
    member: c.member,
    vertices: rotatePoints(c.vertices, rot),
  }));

  // `parts` — the polygons this piece REALLY occupies. For a tile that is its
  // two cartons, not the fused block around them; the gap between two
  // interlocked halves is sheet a neighbour may legally use, and the fused
  // outline claims it. Every geometric decision below (bounds, overlap,
  // hit-test, area) reads parts, so a tile behaves like the two dies it is.
  const parts = rotComps.length ? rotComps.map((c) => c.vertices) : [rotated];
  const bb = bboxOfMany(parts);
  const dx = piece.x - bb.x0;
  const dy = piece.y - bb.y0;
  return {
    outline: translatePoints(rotated, dx, dy),
    components: rotComps.map((c) => ({
      role: c.role,
      member: c.member,
      vertices: translatePoints(c.vertices, dx, dy),
    })),
    parts: parts.map((v) => translatePoints(v, dx, dy)),
    bbox: { x0: piece.x, y0: piece.y, x1: piece.x + bb.w, y1: piece.y + bb.h, w: bb.w, h: bb.h },
    proto: t,
  };
}

/**
 * The die area a piece really consumes — the sum of its parts.
 *
 * For a tile this is the two cartons, NOT the fused block: the sheet between
 * two interlocked halves is not covered by anything, and counting it would
 * quietly inflate every utilization figure the moment tiling appears.
 */
export function partsArea(geometry) {
  return (geometry?.parts || []).reduce((n, v) => n + polygonArea(v), 0);
}

/** Combined bounds of several vertex lists. */
export function bboxOfMany(lists) {
  const all = [];
  for (const l of lists) if (Array.isArray(l)) all.push(...l);
  return bboxOf(all);
}

/** Rotated bounding size of a prototype at `rot`, without placing it. */
export function rotatedSize(proto, rot) {
  const parts = proto.components?.length
    ? proto.components.map((c) => rotatePoints(c.vertices, rot))
    : [rotatePoints(proto.outline, rot)];
  const bb = bboxOfMany(parts);
  return { w: bb ? bb.w : 0, h: bb ? bb.h : 0 };
}

/* ══════════════════════════════════════════════════════════════════════════
 * Projection back to the wire format
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * Project the doc into an /export/vector-shaped result.
 *
 * The point of this function is that NOTHING downstream needs to know the
 * sheet was hand-edited: VectorLayoutView renders it, buildPressPdfPayload
 * plates it, and the stat readers read it. `(x, y)` is already the lower-left
 * of the rotated bbox, so the payload builder's `Math.min(...placed_vertices)`
 * recovers it exactly — the edited sheet and a nested one reduce identically.
 *
 * `overlap_ok` is computed rather than copied: an operator can hand-build an
 * invalid sheet, and interpretVectorResult already refuses to render one.
 */
export function docToVectorResult(doc) {
  // Only the uploads this sheet still uses. `downloadPressPdf` demands one file
  // part per entry in `sources[]`, so carrying a source whose last carton the
  // operator deleted would refuse the export over artwork the plate does not
  // want — and `type_index` must be re-mapped to match, or the projected result
  // would index its own source list wrongly.
  const used = new Set(
    doc.pieces.map((p) => protoOf(doc, p)?.sourceId).filter(Boolean),
  );
  const sources = doc.sources.filter((s) => used.has(s.source_id));
  const indexOfSource = new Map(sources.map((s, i) => [s.source_id, i]));

  const placements = [];
  const instances = new Map();
  let area = 0;
  let cartons = 0;

  doc.pieces.forEach((piece, i) => {
    const g = pieceGeometry(doc, piece);
    if (!g) return;
    const t = g.proto;
    const typeIndex = indexOfSource.has(t.sourceId) ? indexOfSource.get(t.sourceId) : t.typeIndex;
    area += partsArea(g);
    cartons += t.cartons;

    // A COMPOSITE piece (a pair the operator built) exports as one placement
    // PER MEMBER. This is not a stylistic choice: the PDF endpoint stamps one
    // carton per `{source_id, x_mm, y_mm, rotation_deg}` — see
    // export_vector/router.py, `Placement(cartons[p.source_id], x, y, rot)` —
    // so a pair sent as ONE entry would plate ONE carton and silently lose the
    // other. Rotations compose, so a member's absolute angle is its own turn
    // plus the piece's, and its lower-left is its own.
    if (t.members?.length && g.components.length === t.members.length) {
      t.members.forEach((mem, k) => {
        const verts = g.components[k].vertices;
        const n = instances.get(t.key) || 0;
        instances.set(t.key, n + 1);
        placements.push({
          type_index: typeIndex,
          instance: n,
          rotation_deg: normalizeAngle(mem.rot + piece.rot),
          translation: [0, 0],
          placed_vertices: verts,
          cartons: 1,
          source_id: mem.sourceId || t.sourceId || undefined,
          components: [],
          editor_uid: piece.uid,
          editor_index: i,
          editor_member: k,
        });
      });
      return;
    }

    const n = instances.get(t.key) || 0;
    instances.set(t.key, n + 1);
    placements.push({
      type_index: typeIndex,
      instance: n,
      rotation_deg: normalizeAngle(piece.rot),
      translation: [0, 0],
      placed_vertices: g.outline,
      cartons: t.cartons,
      piece_name: t.pieceName || undefined,
      source_id: t.sourceId || undefined,
      components: g.components.map((c) => ({
        role: c.role, member: c.member, placed_vertices: c.vertices,
      })),
      // Editor provenance — ignored by the wire, used to map a rendered
      // polygon back to the piece the operator can select.
      editor_uid: piece.uid,
      editor_index: i,
    });
  });

  const sheetArea = doc.sheet.width * doc.sheet.height;
  const conflicts = computeConflicts(doc);

  return {
    job_id: doc.originJobId || null,
    sheet: {
      width: doc.sheet.width,
      height: doc.sheet.height,
      ...(doc.sheet.margins ? { margins: doc.sheet.margins } : {}),
      kerf: doc.sheet.kerf,
    },
    sources,
    placements,
    ...(doc.prepressReport ? { prepress_report: doc.prepressReport } : {}),
    prepress_applied: !!doc.prepressReport,
    cartons_placed: cartons,
    // Stamps on the plate, which is what `placements` now counts. A hand-built
    // pair is one piece on the sheet and two stamps in the export.
    n_placed: placements.length,
    n_unplaced: 0,
    utilization: sheetArea > 0 ? area / sheetArea : 0,
    overlap_ok: conflicts.overlaps.length === 0,
    runtime_ms: 0,
    edited: true,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
 * Conflicts
 * ════════════════════════════════════════════════════════════════════════ */

/** The printable rectangle — the sheet less its margins. */
export function usableArea(doc) {
  const m = doc?.sheet?.margins || { top: 0, right: 0, bottom: 0, left: 0 };
  return {
    x0: m.left || 0,
    y0: m.bottom || 0,
    x1: (doc?.sheet?.width || 0) - (m.right || 0),
    y1: (doc?.sheet?.height || 0) - (m.top || 0),
  };
}

/**
 * Everything wrong with the sheet as it currently stands.
 *
 * Three kinds, kept separate because they are three different conversations
 * with the operator:
 *
 *   overlaps    two dies share area. The press cannot cut this. Hard error.
 *   tooClose    the gap is under kerf. It will cut, badly. Warning.
 *   outOfBounds a die crosses the gripper/tail/guide strip or the sheet edge.
 *
 * A manual editor without this is a worse tool than the nest it is correcting,
 * because the nest at least guaranteed a valid sheet. Bbox pre-filtering keeps
 * it linear enough to run on every drag frame.
 */
export function computeConflicts(doc) {
  const kerf = Math.max(0, Number(doc?.sheet?.kerf) || 0);
  const usable = usableArea(doc);
  const geo = [];
  for (const piece of doc.pieces || []) {
    const g = pieceGeometry(doc, piece);
    if (g) geo.push({ uid: piece.uid, g });
  }

  const overlaps = [];
  const tooClose = [];
  const outOfBounds = [];
  const bad = new Set();

  for (let i = 0; i < geo.length; i++) {
    const a = geo[i];
    const bb = a.g.bbox;
    // A HAND-BUILT pair is two separate stamps, so the knife needs its clearance
    // between them too — and that gap was solved against the kerf in force when
    // the pair was made. Raise the kerf afterwards and the pair is quietly too
    // tight, with nothing between the two halves to notice it. An engine tile is
    // exempt: its halves are one fused die sharing a cut line, and 0 mm between
    // them is the interlock working, not a fault.
    if (kerf > 0 && a.g.proto.members?.length && a.g.parts.length > 1) {
      let tight = false;
      for (let m = 0; m < a.g.parts.length && !tight; m++) {
        for (let n = m + 1; n < a.g.parts.length; n++) {
          if (boundaryDistance(a.g.parts[m], a.g.parts[n]) < kerf - GEOM_EPS) { tight = true; break; }
        }
      }
      if (tight) {
        tooClose.push([a.uid, a.uid]);
        bad.add(a.uid);
      }
    }
    // Bounds are checked on the bbox, not the outline: a concave die whose
    // notch straddles a margin is still fully inside the strip it must clear.
    if (bb.x0 < usable.x0 - 1e-6 || bb.y0 < usable.y0 - 1e-6
     || bb.x1 > usable.x1 + 1e-6 || bb.y1 > usable.y1 + 1e-6) {
      outOfBounds.push(a.uid);
      bad.add(a.uid);
    }
    for (let j = i + 1; j < geo.length; j++) {
      const b = geo[j];
      if (!bboxesNear(a.g.bbox, b.g.bbox, kerf)) continue;
      // Part against part: a tile is two dies, and a neighbour tucked into the
      // gap between its halves is a legal nest, not a collision.
      let hit = null;
      let gap = Infinity;
      for (const pa of a.g.parts) {
        for (const pb of b.g.parts) {
          if (polygonsOverlap(pa, pb)) { hit = 'overlap'; break; }
          if (kerf > 0) gap = Math.min(gap, boundaryDistance(pa, pb));
        }
        if (hit) break;
      }
      if (hit) {
        overlaps.push([a.uid, b.uid]);
        bad.add(a.uid); bad.add(b.uid);
      } else if (kerf > 0 && gap < kerf - GEOM_EPS) {
        tooClose.push([a.uid, b.uid]);
        bad.add(a.uid); bad.add(b.uid);
      }
    }
  }
  return { overlaps, tooClose, outOfBounds, offending: bad };
}

/* ══════════════════════════════════════════════════════════════════════════
 * Edits — every one returns a NEW doc (undo/redo is snapshot-based)
 * ════════════════════════════════════════════════════════════════════════ */

function withPieces(doc, pieces, extra = {}) {
  return { ...doc, ...extra, pieces };
}

/** Move a set of pieces by (dx, dy) mm. */
export function movePieces(doc, uids, dx, dy) {
  const set = new Set(uids);
  return withPieces(doc, doc.pieces.map((p) => (
    set.has(p.uid) ? { ...p, x: p.x + dx, y: p.y + dy } : p
  )));
}

/** Put one piece's lower-left at an absolute (x, y). */
export function setPiecePosition(doc, uid, x, y) {
  return withPieces(doc, doc.pieces.map((p) => (
    p.uid === uid ? { ...p, x, y } : p
  )));
}

/**
 * Rotate pieces to an absolute angle, or by a delta, PIVOTING ON THEIR OWN
 * CENTRE.
 *
 * Rotating about the stored (x, y) — the bbox corner — would fling a die
 * across the sheet on every 90° tap, because that corner is not a feature of
 * the shape, it is an artefact of the coordinate convention. Holding the
 * centre is what "turn this carton" means to the person clicking.
 */
export function rotatePieces(doc, uids, { to = null, by = 0 } = {}) {
  const set = new Set(uids);
  return withPieces(doc, doc.pieces.map((p) => {
    if (!set.has(p.uid)) return p;
    const before = pieceGeometry(doc, p);
    if (!before) return p;
    const cx = (before.bbox.x0 + before.bbox.x1) / 2;
    const cy = (before.bbox.y0 + before.bbox.y1) / 2;
    const rot = normalizeAngle(to != null ? to : p.rot + by);
    const t = protoOf(doc, p);
    const size = rotatedSize(t, rot);
    return { ...p, rot, x: cx - size.w / 2, y: cy - size.h / 2 };
  }));
}

/** Remove pieces. */
export function removePieces(doc, uids) {
  const set = new Set(uids);
  return withPieces(doc, doc.pieces.filter((p) => !set.has(p.uid)));
}

/** Add one piece of a prototype at (x, y) with rotation `rot`. */
export function addPiece(doc, protoKey, { x = 0, y = 0, rot = 0 } = {}) {
  const t = protoOf(doc, protoKey);
  if (!t) return doc;
  const uid = `p${doc.nextUid}`;
  return withPieces(doc, [...doc.pieces, { uid, proto: t.key, x, y, rot: normalizeAngle(rot) }],
    { nextUid: doc.nextUid + 1 });
}

/** Copy pieces, offset by (dx, dy). Returns { doc, uids } for re-selection. */
export function duplicatePieces(doc, uids, dx = 0, dy = 0) {
  const set = new Set(uids);
  const copies = [];
  let next = doc.nextUid;
  for (const p of doc.pieces) {
    if (!set.has(p.uid)) continue;
    copies.push({ ...p, uid: `p${next++}`, x: p.x + dx, y: p.y + dy });
  }
  if (!copies.length) return { doc, uids: [] };
  return {
    doc: withPieces(doc, [...doc.pieces, ...copies], { nextUid: next }),
    uids: copies.map((c) => c.uid),
  };
}

/**
 * Resize the sheet / margins / kerf.
 *
 * Pieces are NOT moved to follow. Shrinking a sheet under a laid-out die is a
 * fact the operator needs shown, not silently repaired by dragging their work
 * around; computeConflicts flags every piece that no longer fits and they
 * decide what goes.
 */
export function setSheet(doc, patch) {
  const sheet = { ...doc.sheet };
  if (patch.width != null) sheet.width = Math.max(1, Number(patch.width) || 0);
  if (patch.height != null) sheet.height = Math.max(1, Number(patch.height) || 0);
  if (patch.kerf != null) sheet.kerf = Math.max(0, Number(patch.kerf) || 0);
  if ('margins' in patch) sheet.margins = normalizeMargins(patch.margins);
  return { ...doc, sheet };
}

/* ══════════════════════════════════════════════════════════════════════════
 * Placement assistance
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * Find a free lower-left for a new piece: first-fit, bottom-left, on a coarse
 * grid, honouring kerf and the margins.
 *
 * Deliberately dumb — it is not a nester and must not look like one. It exists
 * so "+ Add" drops the carton somewhere sane instead of on top of another one;
 * the operator drags it where they actually want it. Returns the usable
 * origin when nothing is free, and the conflict badge then says so.
 */
export function findFreeSpot(doc, protoKey, rot = 0) {
  const t = protoOf(doc, protoKey);
  const usable = usableArea(doc);
  if (!t) return { x: usable.x0, y: usable.y0 };

  const size = rotatedSize(t, rot);
  const kerf = Math.max(0, Number(doc.sheet.kerf) || 0);
  const step = Math.max(2, Math.min(size.w, size.h) / 3);
  const existing = doc.pieces.map((p) => pieceGeometry(doc, p)).filter(Boolean);

  for (let y = usable.y0; y + size.h <= usable.y1 + 1e-6; y += step) {
    for (let x = usable.x0; x + size.w <= usable.x1 + 1e-6; x += step) {
      const cand = pieceGeometry(doc, { proto: t.key, x, y, rot });
      if (!cand) break;
      let clash = false;
      for (const g of existing) {
        if (!bboxesNear(cand.bbox, g.bbox, kerf)) continue;
        for (const pa of cand.parts) {
          for (const pb of g.parts) {
            if (polygonsOverlap(pa, pb)
             || (kerf > 0 && boundaryDistance(pa, pb) < kerf - GEOM_EPS)) { clash = true; break; }
          }
          if (clash) break;
        }
        if (clash) break;
      }
      if (!clash) return { x, y };
    }
  }
  return { x: usable.x0, y: usable.y0 };
}

/** Which piece is under a sheet-mm point? Topmost (last drawn) wins. */
export function hitTest(doc, x, y) {
  for (let i = doc.pieces.length - 1; i >= 0; i--) {
    const p = doc.pieces[i];
    const g = pieceGeometry(doc, p);
    if (!g) continue;
    const bb = g.bbox;
    if (x < bb.x0 || x > bb.x1 || y < bb.y0 || y > bb.y1) continue;
    if (g.parts.some((part) => pointInPolygon([x, y], part))) return p.uid;
  }
  return null;
}

/** Headline numbers for the editor's stat strip. */
export function docStats(doc) {
  let cartons = 0, area = 0;
  const byLabel = new Map();
  for (const p of doc.pieces) {
    const g = pieceGeometry(doc, p);
    if (!g) continue;
    cartons += g.proto.cartons;
    area += partsArea(g);
    const k = g.proto.label;
    byLabel.set(k, (byLabel.get(k) || 0) + g.proto.cartons);
  }
  const u = usableArea(doc);
  const sheetArea = doc.sheet.width * doc.sheet.height;
  const usableAreaMm = Math.max(0, u.x1 - u.x0) * Math.max(0, u.y1 - u.y0);
  return {
    pieces: doc.pieces.length,
    cartons,
    area,
    utilization: sheetArea > 0 ? area / sheetArea : 0,
    netUtilization: usableAreaMm > 0 ? area / usableAreaMm : 0,
    byLabel: Array.from(byLabel, ([label, n]) => ({ label, cartons: n })),
  };
}

/* ══════════════════════════════════════════════════════════════════════════
 * Snapping
 * ════════════════════════════════════════════════════════════════════════ */

/** How many snap guides to report at once (one per axis is the normal case). */
const SNAP_AXES = ['x', 'y'];

/**
 * Nudge a drag so it lands on something meaningful instead of near it.
 *
 * A hand-placed die that is 0.3 mm off the kerf line is worse than one the
 * nest placed, and no operator can hit 3.000 mm by dragging a mouse. So the
 * drag is pulled onto the lines that actually mean something on a press sheet:
 *
 *   · a neighbour's facing edge PLUS KERF — the legal butt position, which is
 *     where a hand edit is usually trying to go;
 *   · a neighbour's near edge — alignment, so a hand-built row stays a row;
 *   · the usable-area edge — hard against the gripper/guide limit.
 *
 * `tol` is in mm and the caller derives it from zoom, so the pull stays a
 * constant few pixels on screen: it must not become a 20 mm magnet when the
 * operator zooms out to see the whole sheet.
 *
 * Returns the adjusted delta and the guide lines to draw, so the UI can show
 * WHY the die jumped. Snapping that cannot be seen reads as a bug.
 */
export function snapDelta(doc, uids, dx, dy, tol) {
  if (!(tol > 0)) return { dx, dy, guides: [] };
  const moving = new Set(uids);
  let sel = null;
  const others = [];
  for (const p of doc.pieces) {
    const g = pieceGeometry(doc, p);
    if (!g) continue;
    if (moving.has(p.uid)) {
      sel = sel ? {
        x0: Math.min(sel.x0, g.bbox.x0), y0: Math.min(sel.y0, g.bbox.y0),
        x1: Math.max(sel.x1, g.bbox.x1), y1: Math.max(sel.y1, g.bbox.y1),
      } : { ...g.bbox };
    } else {
      others.push(g.bbox);
    }
  }
  if (!sel) return { dx, dy, guides: [] };

  const kerf = Math.max(0, Number(doc.sheet.kerf) || 0);
  const usable = usableArea(doc);
  const out = { dx, dy, guides: [] };

  for (const axis of SNAP_AXES) {
    const lo = axis === 'x' ? sel.x0 + dx : sel.y0 + dy;
    const hi = axis === 'x' ? sel.x1 + dx : sel.y1 + dy;
    const uLo = axis === 'x' ? usable.x0 : usable.y0;
    const uHi = axis === 'x' ? usable.x1 : usable.y1;

    // [line the selection lands on, which selection edge lands on it]
    const targets = [[uLo, 'lo'], [uHi, 'hi']];
    for (const o of others) {
      const oLo = axis === 'x' ? o.x0 : o.y0;
      const oHi = axis === 'x' ? o.x1 : o.y1;
      targets.push([oHi + kerf, 'lo'], [oLo - kerf, 'hi']);   // butt at kerf
      targets.push([oLo, 'lo'], [oHi, 'hi']);                 // align edges
    }

    let best = null;
    for (const [line, edge] of targets) {
      const d = line - (edge === 'lo' ? lo : hi);
      if (Math.abs(d) <= tol && (!best || Math.abs(d) < Math.abs(best.d))) best = { d, line };
    }
    if (best) {
      if (axis === 'x') out.dx += best.d; else out.dy += best.d;
      out.guides.push({ axis, at: best.line });
    }
  }
  return out;
}

/* ══════════════════════════════════════════════════════════════════════════
 * Undo / redo
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * How many edits back the operator can walk.
 *
 * Snapshots, not diffs — a doc is a sheet block, a prototype list and a few
 * numbers per piece, so a hundred of them is cheap, and `sources` (the only
 * bulky part) is shared by reference across every snapshot rather than copied.
 * Diffing would buy nothing and could get an undo wrong, which is the one thing
 * an undo may never do.
 */
export const EDITOR_HISTORY_LIMIT = 100;

/** Fresh editor state around a document. */
export function editorInit(doc) {
  return { doc, past: [], future: [], coalesce: null };
}

/**
 * Commit a new document, pushing the old one onto the undo stack.
 *
 * `coalesce` names the CONTROL being edited. Consecutive commits carrying the
 * same name replace the top of the stack instead of stacking on it, so typing
 * "150" into the sheet width is one undo step, not three.
 *
 * Without it the margin fields would be intolerable: four edges, each a few
 * keystrokes, and an operator who mistyped one would have to press undo a
 * dozen times to get back. Anything that is a single gesture — a drag, a
 * rotation, a delete — passes no key and always stacks.
 */
export function editorApply(state, nextDoc, { coalesce = null } = {}) {
  if (!state || !nextDoc || nextDoc === state.doc) return state;
  if (coalesce && state.coalesce === coalesce && state.past.length) {
    return { ...state, doc: nextDoc, future: [] };
  }
  const past = [...state.past, state.doc];
  return {
    doc: nextDoc,
    past: past.length > EDITOR_HISTORY_LIMIT ? past.slice(past.length - EDITOR_HISTORY_LIMIT) : past,
    future: [],
    coalesce,
  };
}

export function editorUndo(state) {
  if (!state?.past?.length) return state;
  return {
    doc: state.past[state.past.length - 1],
    past: state.past.slice(0, -1),
    future: [state.doc, ...state.future],
    // Walking the history ends the run being typed: the next keystroke must
    // start a new step rather than overwrite the one just restored.
    coalesce: null,
  };
}

export function editorRedo(state) {
  if (!state?.future?.length) return state;
  return {
    doc: state.future[0],
    past: [...state.past, state.doc],
    future: state.future.slice(1),
    coalesce: null,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
 * Tiles built by hand
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * ═══ WHAT A HAND-BUILT TILE IS, AND WHAT IT IS NOT ═════════════════════════
 *
 * A prepress tile is two cartons the PREPRESS ENGINE fused: it may reshape the
 * die (flaps), and it emits the pair as one piece. Nothing on the client can
 * reproduce that, and pretending otherwise would put a die on a plate that
 * nobody computed.
 *
 * What the editor builds is the other honest thing: a PAIR. Two stamps of the
 * SAME carton, one optionally turned, slid together along one axis until they
 * touch at exactly the kerf. The contact is measured against the die's own
 * outline, so where a shape CAN nest into its twin the pair is genuinely
 * narrower than two side by side — but it is a pair the operator composed, not
 * a prepress tile, and the model never calls it one.
 *
 * HOW MUCH IT ACTUALLY BUYS, measured on the 12 real corpus blanks:
 *
 *   across   nothing, on every one of them. The pair is exactly two bounding
 *            boxes plus kerf, because the glue flap and the panel row reach the
 *            full width on both facing edges, so contact is bbox-limited and
 *            turning the second half changes nothing at all.
 *   stacked  a real saving on all of them (41.5 mm on the BL blank), and there
 *            the turn matters for BL and STE — but not for 4 of the 5 RTE
 *            cartons.
 *
 * That is why the arrangement CONTROLS are behind a deployment flag: on a real
 * carton they usually offer a choice that does not change the die. The geometry
 * below is unchanged and still exact — it is the offering of it that is gated.
 *
 * ═══ WHY IT PLATES CORRECTLY ═══════════════════════════════════════════════
 *
 * The PDF endpoint takes `{source_id, x_mm, y_mm, rotation_deg}` and stamps ONE
 * carton per entry (router.py: `Placement(cartons[p.source_id], x, y, rot)`).
 * Two cartons therefore need TWO entries. A composite prototype carries its
 * `members` — each member's own rotation relative to the pair, and its own
 * outline — precisely so `docToVectorResult` can emit one placement per member
 * with the member's absolute rotation and its own lower-left. Rotations compose,
 * so a member of a pair placed at 90° exports as `member.rot + 90`, and the
 * polygon the backend stamps is the polygon the editor drew.
 */

/**
 * Convex hull (monotone chain), CCW.
 *
 * Used only for a composite prototype's `outline`, which is a FRAME — the thing
 * you rotate and measure. Every geometric decision reads `components`/`parts`
 * instead, because the hull spans the gap between two interlocked halves and
 * that gap is sheet a neighbour may use.
 */
export function convexHull(pts) {
  const p = pts.map((q) => [q[0], q[1]]).sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));
  if (p.length < 3) return p;
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const half = (src) => {
    const out = [];
    for (const q of src) {
      while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], q) <= 0) out.pop();
      out.push(q);
    }
    return out;
  };
  const lower = half(p);
  const upper = half(p.slice().reverse());
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

/**
 * Slide B toward A along one axis until it can go no further.
 *
 * Not a binary search on "is this position legal". Clearance is NOT monotonic
 * in the offset — a die with a notch separates, touches, separates again as its
 * twin nests in, then collides — so bisection would answer with whichever
 * boundary it happened to bracket. Physically you slide until you hit
 * something, so this scans inward from clear and stops at the FIRST collision,
 * then refines that one contact to 0.1 µm.
 */
function slideUntilContact(A, B, axis, kerf, hi) {
  const bbA = bboxOf(A);
  const shift = (t) => translatePoints(B, axis === 'x' ? t : 0, axis === 'x' ? 0 : t);
  const ok = (t) => {
    const b = shift(t);
    const bb = bboxOf(b);
    if (!bboxesNear(bbA, bb, kerf)) return true;     // nowhere near — no work needed
    for (const pa of [A]) {
      if (polygonsOverlap(pa, b)) return false;
      if (kerf > 0 && boundaryDistance(pa, b) < kerf - GEOM_EPS) return false;
    }
    return true;
  };
  if (!ok(hi)) return hi;
  const step = Math.max(0.05, hi / 200);
  let good = hi;
  for (let t = hi - step; t > 0; t -= step) {
    if (!ok(t)) {
      let lo = t, gd = good;
      for (let i = 0; i < 40 && gd - lo > 1e-4; i++) {
        const mid = (lo + gd) / 2;
        if (ok(mid)) gd = mid; else lo = mid;
      }
      return gd;
    }
    good = t;
  }
  return good;
}

/** The arrangements the Add palette offers. */
export const TILE_AXES = ['x', 'y'];
export const TILE_TURNS = [0, 180];

/**
 * Two copies of one die, interlocked into a pair.
 *
 * @param outline  the die at 0°, bbox-min on the origin
 * @param axis     'x' to set them across, 'y' to stack them
 * @param turn     rotation applied to the SECOND copy (180° is the classic
 *                 head-to-head that lets a tapered carton nest)
 * @param kerf     required clearance between the two, mm
 * @returns { members, outline, w, h } in a fresh local frame, bbox-min on the origin
 */
export function tilePair(outline, { axis = 'x', turn = 180, kerf = 0 } = {}) {
  const A = outline;
  const bbA = bboxOf(A);
  const rotB = normalizeAngle(turn);
  let B = rotatePoints(A, rotB);
  const bbB = bboxOf(B);

  // Square up on the perpendicular axis (centres aligned) and start the second
  // copy clear of the first, then close the gap.
  if (axis === 'x') {
    B = translatePoints(B, -bbB.x0, ((bbA.y0 + bbA.y1) - (bbB.y0 + bbB.y1)) / 2);
  } else {
    B = translatePoints(B, ((bbA.x0 + bbA.x1) - (bbB.x0 + bbB.x1)) / 2, -bbB.y0);
  }
  const hi = (axis === 'x' ? bbA.x1 : bbA.y1) + Math.max(0, kerf);
  const t = slideUntilContact(A, B, axis, Math.max(0, kerf), hi);
  const Bp = translatePoints(B, axis === 'x' ? t : 0, axis === 'x' ? 0 : t);

  // Re-origin the pair so its own bbox minimum sits at (0, 0), like every
  // other prototype.
  const bbU = bboxOfMany([A, Bp]);
  const shift = (v) => translatePoints(v, -bbU.x0, -bbU.y0);
  const members = [
    { role: 'A', member: 0, rot: 0, vertices: shift(A) },
    { role: 'B', member: 1, rot: rotB, vertices: shift(Bp) },
  ];
  return {
    members,
    outline: convexHull(members.flatMap((m) => m.vertices)),
    w: bbU.w,
    h: bbU.h,
  };
}

/**
 * Carton families whose prepress tile is SELF-MATED: both halves are the SAME
 * blank, differing only by an offset and possibly a rotation. No panel is
 * resequenced, so a pair the operator arranges by hand is the same die the
 * press would cut — just placed by them rather than by the package.
 *
 * BL and IL are NOT here, and that is the whole distinction. Their tile is
 * FUSED: prepress resequences the panels of one half (the measured pair puts
 * member B in variant RBLM/F, moving its Right panel 228 mm from its siblings).
 * Sliding two standard BL blanks together produces a valid layout but not a BL
 * tile, so offering an arrangement choice there would imply a control over
 * something the choice does not reach.
 *
 * An UNKNOWN family is treated as fused. Being wrong in that direction costs an
 * option; being wrong the other way puts an unresequenced die on a plate.
 */
export const SELF_MATED_FAMILIES = ['RTE', 'STE'];

/**
 * May the operator arrange this carton's pair themselves?
 *
 * No carton type at all means prepress never touched it — there is no blank and
 * no interlock to respect, so a hand-built pair is the only thing there is.
 */
export function acceptsCustomPair(cartonType) {
  if (!cartonType) return true;
  return SELF_MATED_FAMILIES.includes(String(cartonType).toUpperCase());
}

/**
 * Does the editor OFFER this carton a pair the operator arranges themselves?
 *
 * Two questions, and they are different:
 *
 *   CAN it            acceptsCustomPair — a fact about the family's geometry.
 *   SHOULD we offer   this — a deployment's call, because on real cartons the
 *                     arrangement controls usually change nothing (see tilePair).
 *
 * A carton prepress never touched is always offered one: there is no tile to
 * compare against and no interlock to respect, so a hand-built pair is the only
 * thing available and the flag has no business suppressing it.
 */
export function offersCustomPair(cartonType, allowArrangement) {
  if (!cartonType) return true;
  return acceptsCustomPair(cartonType) && !!allowArrangement;
}

/** Can this prototype be paired? Only a single carton whose upload we know. */
export function isTileable(proto) {
  return !!proto && proto.cartons === 1 && !!proto.sourceId && proto.origin !== 'tile';
}

/**
 * The key a given pair of a given base carton lives under.
 *
 * KERF IS PART OF THE IDENTITY. The interlock is solved against it, so "a pair
 * of Alpha across, turned" is a different die at 0 mm kerf than at 3 mm. Keying
 * without it would silently hand back a pair solved for the old clearance the
 * next time one was added after a kerf change.
 */
export function tilePrototypeKey(baseKey, { axis = 'x', turn = 180, kerf = 0 } = {}) {
  return `${baseKey}@pair:${axis}:${normalizeAngle(turn)}:k${Math.round((Number(kerf) || 0) * 1000) / 1000}`;
}

/**
 * Make sure the doc holds the pair prototype for `baseKey`, and return its key.
 *
 * Idempotent: asking twice reuses the one prototype, so ten pairs of one carton
 * share one entry and one geometry computation. Returns the doc unchanged when
 * the base is not pairable.
 */
export function ensureTilePrototype(doc, baseKey, opts = {}) {
  const base = protoOf(doc, baseKey);
  if (!isTileable(base)) return { doc, key: null };
  const axis = TILE_AXES.includes(opts.axis) ? opts.axis : 'x';
  const turn = normalizeAngle(opts.turn ?? 180);
  const kerf = Math.max(0, Number(opts.kerf ?? doc.sheet.kerf) || 0);
  const key = tilePrototypeKey(base.key, { axis, turn, kerf });
  if (protoOf(doc, key)) return { doc, key };

  const pair = tilePair(base.outline, { axis, turn, kerf });
  const proto = {
    key,
    sourceId: base.sourceId,
    pieceName: null,
    typeIndex: base.typeIndex,
    label: base.label,
    cartons: 2,
    outline: pair.outline,
    // Drawn from the members, so the picture and the plate cannot disagree.
    components: pair.members.map((m) => ({ role: m.role, member: m.member, vertices: m.vertices })),
    // The EXPORT contract for a composite piece: one placement per member.
    members: pair.members.map((m) => ({ sourceId: base.sourceId, rot: m.rot, role: m.role })),
    w: pair.w,
    h: pair.h,
    origin: 'tile',
    tile: { axis, turn, kerf, base: base.key },
  };
  return { doc: { ...doc, prototypes: [...doc.prototypes, proto] }, key };
}

/** Add a hand-built pair of `baseKey` to the sheet. Returns { doc, uid }. */
export function addTile(doc, baseKey, { axis = 'x', turn = 180, rot = 0, x = null, y = null } = {}) {
  const made = ensureTilePrototype(doc, baseKey, { axis, turn });
  if (!made.key) return { doc, uid: null };
  const spot = (x != null && y != null)
    ? { x, y }
    : findFreeSpot(made.doc, made.key, rot);
  const next = addPiece(made.doc, made.key, { ...spot, rot });
  return { doc: next, uid: next.pieces[next.pieces.length - 1].uid };
}


/* ══════════════════════════════════════════════════════════════════════════
 * Tiles built by PREPRESS
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * The key a prepress-built tile of a given carton lives under.
 *
 * No config or arrangement in it: prepress decides the arrangement from the
 * carton and the job's own configuration, so there is exactly one prepress tile
 * per carton and asking twice must return the same one.
 */
export function prepressTileKey(sourceId) {
  return `${sourceId}@prepress-pair`;
}

/**
 * Add a tile that PREPRESS built (see requestPrepressTile / POST
 * /export/vector/tile) to the document.
 *
 * ═══ WHY THIS IS NOT `addTile` ═════════════════════════════════════════════
 *
 * `addTile` slides two copies of a die together and is honest for a plain die.
 * A BL tile is not that. Prepress FUSES two blanks and resequences the panels of
 * one of them — the measured pair puts member B in variant RBLM/F, whose Right
 * panel moves 228 mm from its siblings — under rules and configuration nothing
 * on the client can reproduce. So the geometry arrives from the server and this
 * only files it.
 *
 * ═══ WHAT GETS FILED, AND WHY IT IS SPLIT IN TWO ═══════════════════════════
 *
 * The tile's SHAPE becomes a prototype, so it draws, nests and hit-tests like
 * any other piece. Its `members` is deliberately null: a hand-built pair exports
 * as one placement per member, but a prepress tile is ONE placed piece worth two
 * cartons, and the export composes its halves from `transforms.tile_members` —
 * the same path a nested tile takes.
 *
 * The tile's TRANSFORM CHAIN is merged into the report, and only the
 * tile-specific parts of it: `transforms.tile_members` and `assets.tile`. Pose,
 * blank_origin and the standard panel map are left exactly as the nest recorded
 * them. Replacing the whole entry would re-derive the artwork placement of every
 * SINGLE blank already on the sheet from a freshly computed chain, and if the
 * two disagreed by so much as a flap dimension, cartons the operator never
 * touched would quietly move.
 */
export function addPrepressTile(doc, tile, { x = null, y = null, rot = 0 } = {}) {
  const sourceId = tile?.source_id;
  const verts = tile?.vertices;
  if (!sourceId || !Array.isArray(verts) || verts.length < 3) return { doc, uid: null };

  const key = prepressTileKey(sourceId);
  let next = doc;

  if (!protoOf(doc, key)) {
    // Re-origin the tile and its members through ONE shift, so the halves
    // cannot drift from the outline they sit in.
    const bb = bboxOf(verts);
    const shift = (v) => translatePoints(v, -bb.x0, -bb.y0);
    const outline = shift(verts);
    const comps = (Array.isArray(tile.components) ? tile.components : [])
      .filter((c) => Array.isArray(c?.vertices) && c.vertices.length >= 3)
      .map((c, i) => ({
        role: typeof c.role === 'string' ? c.role : null,
        member: Number.isInteger(c.member) ? c.member : i,
        vertices: shift(c.vertices),
      }));
    const base = doc.prototypes.find((t) => t.sourceId === sourceId);
    next = {
      ...next,
      prototypes: [...next.prototypes, {
        key,
        sourceId,
        pieceName: tile.piece_name || null,
        typeIndex: base ? base.typeIndex : 0,
        label: base ? base.label : sourceId,
        cartons: Number.isInteger(tile.cartons) && tile.cartons > 0 ? tile.cartons : 2,
        outline,
        components: comps,
        // NOT a composite for export purposes — see the note above.
        members: null,
        w: bb.w,
        h: bb.h,
        origin: 'prepress-tile',
        cartonType: tile.carton_type || null,
        variants: comps.length
          ? (tile.components || []).map((c) => c.variant || null)
          : null,
      }],
    };
  }

  next = { ...next, prepressReport: mergeTileIntoReport(next.prepressReport, tile) };

  const spot = (x != null && y != null) ? { x, y } : findFreeSpot(next, key, rot);
  const placed = addPiece(next, key, { ...spot, rot });
  return { doc: placed, uid: placed.pieces[placed.pieces.length - 1].uid };
}

/**
 * Fold a tile's own chain into the report the doc already holds.
 *
 * Only `transforms.tile_members` and `assets.tile` — the parts that describe
 * the TILE. See addPrepressTile for why the rest of the entry is left alone.
 * When the carton has no entry at all (prepress never ran on it in this job),
 * the returned entry is taken whole, because there is nothing to preserve.
 */
export function mergeTileIntoReport(report, tile) {
  const entry = tile?.report;
  const name = tile?.source_id;
  if (!entry || !name) return report;
  const cartons = Array.isArray(report?.cartons) ? report.cartons : [];
  const i = cartons.findIndex((c) => c && String(c.name) === name);
  if (i < 0) return { ...(report || {}), cartons: [...cartons, entry] };

  const cur = cartons[i];
  const merged = {
    ...cur,
    transforms: {
      ...(cur.transforms || {}),
      ...(entry.transforms?.tile_members
        ? { tile_members: entry.transforms.tile_members }
        : {}),
    },
    assets: {
      ...(cur.assets || {}),
      ...(entry.assets?.tile ? { tile: entry.assets.tile } : {}),
    },
  };
  const out = cartons.slice();
  out[i] = merged;
  return { ...(report || {}), cartons: out };
}

/** Does this doc already hold a prepress tile for `sourceId`? */
export function hasPrepressTile(doc, sourceId) {
  return !!protoOf(doc, prepressTileKey(sourceId));
}


/* ══════════════════════════════════════════════════════════════════════════
 * Selecting more than one thing at a time
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * Every piece cut from one uploaded carton — "all the Brilliance on this sheet".
 *
 * Matched on `sourceId`, which is the upload, so it gathers a carton's singles
 * AND its tiles: a Brilliance blank and a Brilliance pair are both Brilliance,
 * and an operator asking for "all of them" means all of them. Prototype key
 * would have split those apart, which is the one grouping nobody wants.
 */
export function piecesOfSource(doc, sourceId) {
  if (!sourceId) return [];
  return doc.pieces
    .filter((p) => protoOf(doc, p)?.sourceId === sourceId)
    .map((p) => p.uid);
}

/**
 * Every piece of one carton FAMILY — "all the RTE on this sheet".
 *
 * A different question from the one above, and the vocabulary is the codebase's
 * own: a carton is an upload (Brilliance), a carton TYPE is the structural
 * family (BL, IL, RTE, STE). Cartons prepress never classified have no family
 * and are never gathered by this.
 */
export function piecesOfCartonType(doc, cartonType) {
  if (!cartonType) return [];
  const want = String(cartonType).toUpperCase();
  return doc.pieces
    .filter((p) => String(protoOf(doc, p)?.cartonType || '').toUpperCase() === want)
    .map((p) => p.uid);
}

/** What distinct cartons and families are on the sheet, with their counts. */
export function selectableGroups(doc) {
  const sources = new Map();
  const types = new Map();
  for (const p of doc.pieces) {
    const t = protoOf(doc, p);
    if (!t) continue;
    if (t.sourceId) {
      const cur = sources.get(t.sourceId) || { id: t.sourceId, label: t.label, pieces: 0 };
      cur.pieces += 1;
      sources.set(t.sourceId, cur);
    }
    if (t.cartonType) {
      const cur = types.get(t.cartonType) || { id: t.cartonType, pieces: 0 };
      cur.pieces += 1;
      types.set(t.cartonType, cur);
    }
  }
  return { sources: Array.from(sources.values()), types: Array.from(types.values()) };
}

/**
 * Every piece the rectangle touches.
 *
 * TOUCH, not containment: a marquee that only takes what it fully encloses
 * makes an operator drag past the sheet edge to catch a die sitting on it, and
 * on a full sheet there is nowhere to start the drag. Tested against the
 * piece's PARTS, so lassoing the empty gap inside a stacked pair selects
 * nothing — the same rule hit-testing already follows.
 */
export function piecesInRect(doc, x0, y0, x1, y1) {
  const rx0 = Math.min(x0, x1), rx1 = Math.max(x0, x1);
  const ry0 = Math.min(y0, y1), ry1 = Math.max(y0, y1);
  const rect = [[rx0, ry0], [rx1, ry0], [rx1, ry1], [rx0, ry1]];
  const rectBB = { x0: rx0, y0: ry0, x1: rx1, y1: ry1 };
  const out = [];
  for (const p of doc.pieces) {
    const g = pieceGeometry(doc, p);
    if (!g || !bboxesNear(g.bbox, rectBB, 0)) continue;
    const hit = g.parts.some((part) => (
      // Wholly inside, or genuinely overlapping. The containment case is
      // checked first because a small die swallowed by a big marquee crosses
      // no edge at all.
      part.every(([x, y]) => x >= rx0 && x <= rx1 && y >= ry0 && y <= ry1)
      || polygonsOverlap(part, rect)
    ));
    if (hit) out.push(p.uid);
  }
  return out;
}

/** Union of two uid lists, order-stable, no duplicates. */
export function unionSelection(a, b) {
  const seen = new Set(a);
  return [...a, ...b.filter((u) => !seen.has(u))];
}


/**
 * Why the press-ready export must not run — or null when it may.
 *
 * ONE rule, returning the REASON, because the editor previously carried two
 * expressions for this: a boolean that disabled the button and a separate
 * ternary that wrote its tooltip. They had already drifted — an empty sheet
 * disabled the button while the tooltip still offered to "gang the edited
 * sheet", and OUT OF BOUNDS disabled nothing at all despite being counted as
 * an issue and drawn on the canvas. A die crossing the gripper, tail or guide
 * strip cannot be printed (the press holds the sheet there) and one crossing
 * the sheet edge does not exist, so it is a hard defect, not an advisory —
 * `issues` tones it `danger` for the same reason.
 *
 * Kerf violations stay advisory on purpose: `tooClose` means two dies are
 * nearer than the knife clearance, which is a quality judgement a plant can
 * make for itself, and `tightWithin` names a pair to rebuild. Neither makes
 * the sheet impossible to cut.
 *
 * @returns {string|null} operator copy naming the fix, or null to allow export
 */
export function exportBlockReason({ conflicts, missingArt = [], unplaceable = [], pieceCount = 0 } = {}) {
  const c = conflicts || {};
  const n = (v) => (Array.isArray(v) ? v.length : 0);
  if (pieceCount === 0) return 'Nothing on the sheet to export yet.';
  if (n(c.overlaps)) return 'Resolve the overlaps first — an overlapping sheet is not cuttable.';
  if (n(c.outOfBounds)) {
    return `${n(c.outOfBounds)} carton${n(c.outOfBounds) > 1 ? 's cross' : ' crosses'} `
      + 'the sheet edge or a gripper/tail/guide strip. Move it inside the usable '
      + 'area — the press cannot cut there.';
  }
  if (n(missingArt)) return 'Some cartons on the sheet have no PDF on the bench.';
  if (n(unplaceable)) return 'A carton\u2019s prepress transform chain could not be read — see Checks.';
  return null;
}
